import { useQuery } from '@tanstack/react-query'

import { getActiveChainId } from '@/config/active'
import { isNutrimaticConfigured } from '@/config/nutrimatic'
import { fetchNutrimaticTokenProfile } from '@/data/nutrimatic/fetchMarket'

import { MARKET_QUERY_CACHE } from '../cachePolicy'
import {
  isWrappedNativeAddress,
  toCatalogAddress,
  toOnChainAddress,
} from '../normalizeMarketToken'
import { resolveOfflineTokenProfile } from '../resolveTokenProfile'
import {
  resolveTokenProfileQueryEnabled,
  shouldUseOfflineTokenProfile,
} from '../resolveTokenProfileQuery'
import { marketApiQueryKeys } from '../queryKeys'
import type { MarketTokenProfile } from '../types'

export function useTokenProfile(address: string | undefined) {
  const configured = isNutrimaticConfigured()
  const chainId = getActiveChainId()
  const catalogAddress = address ? toCatalogAddress(address, chainId) : ''
  const fetchAddress = address
    ? isWrappedNativeAddress(address, chainId)
      ? toOnChainAddress(address, chainId)
      : address
    : ''
  const offlineProfile = address ? resolveOfflineTokenProfile(address, chainId) : null
  // Vitrina: fetch whenever SDK is configured — do not gate on wallet chain match.
  const serviceReady = configured

  return useQuery({
    queryKey: marketApiQueryKeys.tokenProfile(catalogAddress),
    queryFn: async (): Promise<MarketTokenProfile | null> => {
      if (!address) return null
      if (shouldUseOfflineTokenProfile(configured) || !serviceReady) {
        return offlineProfile
      }
      const profile = await fetchNutrimaticTokenProfile(fetchAddress)
      if (!profile) return offlineProfile
      return {
        ...profile,
        address: catalogAddress,
      }
    },
    enabled: resolveTokenProfileQueryEnabled({
      address,
      configured,
      isAvailable: serviceReady,
      offlineProfile,
    }),
    placeholderData: (previousData) => previousData,
    ...MARKET_QUERY_CACHE.tokenProfile,
  })
}
